import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CUSTOM_RPC_BY_CHAIN,
  DEFAULT_CHAIN,
  SUPPORTED_CHAIN_IDS,
} from '@package/smart-wallet/constants';
import { createSmartAccountClient, SmartAccountClient } from 'permissionless';
import { toSafeSmartAccount } from 'permissionless/accounts';
import { createPimlicoClient } from 'permissionless/clients/pimlico';
import { prepareUserOperationForErc20Paymaster } from 'permissionless/experimental/pimlico';
import { Address, createPublicClient, http } from 'viem';
import { entryPoint07Address } from 'viem/account-abstraction';
import { useAccount, useWalletClient } from 'wagmi';

/* Constants */
const PIMLICO_URI = 'https://api.pimlico.io/v2';
const PIMLICO_SAFE_VERSION = '1.4.1';

/* Hook */
export const usePimlico = () => {
  const { address, isConnected, chain = DEFAULT_CHAIN } = useAccount();
  const [isLoadingSafeWallet, setisLoadingSafeWallet] = useState(false);
  const [smartAccountAddress, setSmartAccountAddress] = useState<Address>();
  const [smartAccountClient, setSmartAccountClient] =
    useState<SmartAccountClient>();
  const { data: walletClient } = useWalletClient({
    chainId: chain.id,
  });

  /* Memo */
  const pimlicoUrl = useMemo(
    () =>
      `${PIMLICO_URI}/${chain.id}/rpc?apikey=${
        import.meta.env.VITE_PIMLICO_KEY
      }`,
    [chain.id],
  );

  const customRPC = useMemo(() => {
    if (!chain) return;
    return CUSTOM_RPC_BY_CHAIN[chain.id as keyof typeof CUSTOM_RPC_BY_CHAIN];
  }, [chain]);

  const publicClient = useMemo(() => {
    if (
      !chain ||
      !Object.values(SUPPORTED_CHAIN_IDS).some(
        (chainId) => chainId === chain.id,
      )
    )
      return undefined;
    return createPublicClient({
      transport: http(customRPC),
      chain,
    });
  }, [chain, customRPC]);

  const pimlicoClient = useMemo(() => {
    return createPimlicoClient({
      transport: http(pimlicoUrl),
      entryPoint: {
        address: entryPoint07Address,
        version: '0.7',
      },
    });
  }, [pimlicoUrl]);

  /* Callbacks */
  const getSmartAccountClient = useCallback(async () => {
    if (!address || !isConnected || !publicClient || !walletClient) return;
    const smartAccountSafeSigner = await toSafeSmartAccount({
      client: publicClient,
      owners: [walletClient],
      version: PIMLICO_SAFE_VERSION,
      entryPoint: {
        address: entryPoint07Address,
        version: '0.7',
      },
      address: smartAccountAddress as Address,
    });

    return createSmartAccountClient({
      account: smartAccountSafeSigner,
      chain,
      bundlerTransport: http(pimlicoUrl, {
        timeout: 60_000,
      }),
      paymaster: pimlicoClient,
      userOperation: {
        estimateFeesPerGas: async () =>
          (await pimlicoClient.getUserOperationGasPrice()).fast,
        prepareUserOperation:
          prepareUserOperationForErc20Paymaster(pimlicoClient),
      },
    });
  }, [
    address,
    isConnected,
    publicClient,
    walletClient,
    smartAccountAddress,
    chain,
    pimlicoUrl,
    pimlicoClient,
  ]);

  /* Effects */
  useEffect(() => {
    (async () => {
      if (!isConnected || isLoadingSafeWallet || !!smartAccountAddress) return;
      setisLoadingSafeWallet(() => true);
      const smartAccountClient = await getSmartAccountClient();
      setSmartAccountClient(smartAccountClient);
      setSmartAccountAddress(smartAccountClient?.account.address);
      setisLoadingSafeWallet(() => false);
    })();
  }, [
    getSmartAccountClient,
    smartAccountAddress,
    isLoadingSafeWallet,
    isConnected,
  ]);

  /* Return */
  return {
    smartAccountClient,
    smartAccountAddress,
    publicClient,
    pimlicoClient,
    isLoadingSafeWallet,
    getSmartAccountClient,
    setSmartAccountAddress,
  };
};
